import { makeAutoObservable } from 'mobx';
import CreateRmaSessionStore from './create-rma-session-store';
import CreateRmaSettingsStore from './create-rma-settings-store';
import OrderStore from './order-store';
import RmaRequestStore, { ICreateRmaDto } from './rma-request-store';
import {
    CREATE_RMA_DRAFT_BLOCK_REASON,
    type IFulfillmentBlockingReason,
} from './create-rma-submission-types';

export const DRAFT_STATUS = {
    IDLE: 'IDLE',
    SUBMITTING: 'SUBMITTING',
    CREATED: 'CREATED',
    FAILED: 'FAILED',
} as const;

export type DraftStatus = typeof DRAFT_STATUS[keyof typeof DRAFT_STATUS];

export class CreateRmaDraftStore {
    private _requestStore: RmaRequestStore;
    private _status: DraftStatus = DRAFT_STATUS.IDLE;

    constructor(
        private readonly _orderStore: OrderStore,
        private readonly _index: number,
        private readonly _settings: CreateRmaSettingsStore,
        private readonly _session: CreateRmaSessionStore,
    ) {
        makeAutoObservable(this, {}, { autoBind: true });
        this._requestStore = new RmaRequestStore(this._orderStore, this._settings, this._session);
    }

    /** 1-based position among the drafts (FR4). */
    public get index(): number { return this._index; }
    public get fulfillmentLabel(): string { return this._orderStore.orderName; }
    public get orderName(): string { return this._orderStore.orderName; }
    public get orderStore(): OrderStore { return this._orderStore; }
    public get requestStore(): RmaRequestStore { return this._requestStore; }
    public get hasSelectedItems(): boolean { return this._requestStore.requestProducts.length > 0; }
    public get isRMACreated(): boolean { return this._status === DRAFT_STATUS.CREATED; }

    public isValid(): boolean {
        return this.getBlockingReason() === null;
    }

    /**
     * Store-derived submit gates for this draft (shipping, balance, policy-led blocks).
     * Item-level validation (return-method / resolution conflicts, drawer fields) is enforced
     * page-wide via validationGroup.isValid() on the submit bar without per-fulfillment attribution.
     */
    public getBlockingReason(): IFulfillmentBlockingReason | null {
        if (!this.hasSelectedItems) {
            return null;
        }

        const requestStore = this._requestStore;

        if (
            requestStore.dynamicShippingOptionsStore.isLoading
            || requestStore.eligibilityStore.isInProgress
            || requestStore.balancePreviewStore.isInProgress
        ) {
            return { draftIndex: this._index, reasonKey: CREATE_RMA_DRAFT_BLOCK_REASON.LOADING };
        }

        if (requestStore.dynamicShippingOptionsStore.shouldSelectShippingOption) {
            return {
                draftIndex: this._index,
                reasonKey: CREATE_RMA_DRAFT_BLOCK_REASON.SELECT_SHIPPING_OPTION,
            };
        }

        if (requestStore.balancePreviewStore.isCreateRmaBlocked) {
            return {
                draftIndex: this._index,
                reasonKey: CREATE_RMA_DRAFT_BLOCK_REASON.CUSTOMER_PAYMENT_REQUIRED,
            };
        }

        if (this._settings.isPolicyLedMode) {
            if (requestStore.eligibilityStore.isOutOfPolicy) {
                return {
                    draftIndex: this._index,
                    reasonKey: CREATE_RMA_DRAFT_BLOCK_REASON.OUT_OF_POLICY,
                };
            }

            if (this._orderStore.isCreateRmaNotAllowed) {
                return {
                    draftIndex: this._index,
                    reasonKey: CREATE_RMA_DRAFT_BLOCK_REASON.MULTIPLE_RETURNS,
                };
            }
        }

        return null;
    }

    public requestModel(): ICreateRmaDto {
        return this._requestStore.requestModel();
    }

    public markSubmitting(): void {
        this._status = DRAFT_STATUS.SUBMITTING;
    }

    public markCreated(): void {
        this._status = DRAFT_STATUS.CREATED;
    }

    public markFailed(): void {
        this._status = DRAFT_STATUS.FAILED;
    }

    public resetFromSubmitting(): void {
        if (this._status === DRAFT_STATUS.SUBMITTING) {
            this._status = DRAFT_STATUS.IDLE;
        }
    }

    public __destroy(): void {
        this._requestStore.__destroy();
    }
}
